import { ExtractComponentProp } from '@approvalmax/types';
import { AlertIcon, validators } from '@approvalmax/ui';
import { Alert, Box, Grid, Label, RichEditor, Select, Switch, Text } from '@approvalmax/ui/src/components';
import { constants } from 'modules/common';
import { memo, useCallback, useMemo } from 'react';
import { useIsEmailContactLimited } from 'shared/hooks';

import { AttachmentList } from '../../../../components/AttachmentList/AttachmentList';
import { AttachmentKind } from '../../../../data/AttachmentKind';
import { useEmailToSupplierFormQa, useFormHandlers, useValidations } from '../../EmailToSupplierForm.hooks';
import { EmailToSupplierContactPersonData } from '../../EmailToSupplierForm.types';
import { getNewEmailValues } from './Form.helpers';
import { FormProps } from './Form.types';

export const Form = memo<FormProps>((props) => {
    const {
        data,
        onChangeData,
        labels,
        onVisibilityToggle,
        isReadonly,
        attachments,
        attachmentsNew,
        onAttachRemove,
        isVisible,
        hasErrors,
        integrationType,
        getMessage,
        emailOptions,
        customMentionConfig,
        body,
        subject,
        handleClickOnAttachment,
        toCreateValidatorFunction,
        toValidatorFunction,
        ccCreateValidatorFunction,
        ccValidatorFunction,
    } = props;

    const { qaForm, qaFormToggle, qaTo, qaCc, qaSubject, qaBody } = useEmailToSupplierFormQa(integrationType);

    const { onChangeBody, onChangeSubject, onChangeTo, onChangeCc } = useFormHandlers(data, onChangeData);

    const isEmailContactLimited = useIsEmailContactLimited();
    const isDisabled = isReadonly || isEmailContactLimited;

    const { handleToValidate, handleCcValidate } = useValidations({
        toCreateValidatorFunction,
        toValidatorFunction,
        ccCreateValidatorFunction,
        ccValidatorFunction,
    });

    const validateEmail = useCallback<ExtractComponentProp<typeof Select, 'validateCreatedValue'>>((value) => {
        const isValidValue = validators.isEmail(value);

        return {
            isValid: isValidValue,
            errorMessage: isValidValue ? undefined : `${value} is not a valid email`,
        };
    }, []);

    const handleChangeTo = useCallback(
        (value: string[]) => {
            const values = getNewEmailValues(value, emailOptions);

            onChangeTo(values);
        },
        [onChangeTo, emailOptions]
    );

    const handleChangeCc = useCallback(
        (value: string[]) => {
            const values = getNewEmailValues(value, emailOptions);

            onChangeCc(values);
        },
        [onChangeCc, emailOptions]
    );

    const toEmailOptions = useMemo<EmailToSupplierContactPersonData[]>(() => {
        const optionsFromToValue: EmailToSupplierContactPersonData[] = (data?.to ?? []).map((value) => ({
            id: value,
            text: value,
            firstName: '',
            lastName: '',
            emailAddress: value,
            includeInEmails: true,
        }));

        return [...emailOptions, ...optionsFromToValue];
    }, [emailOptions, data?.to]);

    const ccEmailOptions = useMemo<EmailToSupplierContactPersonData[]>(() => {
        const optionsFromCcValue: EmailToSupplierContactPersonData[] = (data?.cc ?? []).map((value) => ({
            id: value,
            text: value,
            firstName: '',
            lastName: '',
            emailAddress: value,
            includeInEmails: true,
        }));

        return [...emailOptions, ...optionsFromCcValue];
    }, [emailOptions, data?.cc]);

    return (
        <Box spacing='16 60' data-qa={qaForm}>
            <Box spacing='16 0'>
                <Grid gridTemplateColumns='max-content auto' alignItems='center' gap={16}>
                    <Text font='headline' fontSize='xxsmall' fontWeight='medium'>
                        {getMessage('formTitle')}
                    </Text>

                    <Switch checked={isVisible} onChange={onVisibilityToggle} size='small' data-qa={qaFormToggle}>
                        {getMessage('toggleLabel')}
                    </Switch>
                </Grid>

                {isEmailContactLimited && (
                    <Box spacing='12 0'>
                        <Alert startIcon={<AlertIcon />} size='small'>
                            {getMessage('sectionNote')}
                        </Alert>
                    </Box>
                )}
            </Box>

            {isVisible && data && (
                <Grid gridTemplateColumns='1fr 1fr' alignItems='flex-start' gap={16}>
                    <Grid rowGap={24}>
                        <Grid gridTemplateColumns='1fr 1fr' gap={16}>
                            <Select
                                label={getMessage('toLabel')}
                                required
                                items={toEmailOptions}
                                multiple
                                itemNameKey='text'
                                value={data.to}
                                autocomplete
                                creatable
                                validateCreatedValue={validateEmail}
                                invalid={hasErrors && !handleToValidate(data)}
                                placeholder={getMessage('toPlaceholder')}
                                size='small'
                                disabled={isDisabled}
                                onChange={handleChangeTo}
                                qa={qaTo}
                            />

                            <div>
                                <Label>{getMessage('fromLabel')}</Label>

                                <Text font='label' fontSize='small' spacing='12 0 0 0'>
                                    {data.from}
                                </Text>
                            </div>
                        </Grid>

                        <Grid gridTemplateColumns='1fr 1fr' gap={16}>
                            <Select
                                label={getMessage('ccLabel')}
                                items={ccEmailOptions}
                                multiple
                                itemNameKey='text'
                                value={data.cc}
                                autocomplete
                                creatable
                                validateCreatedValue={validateEmail}
                                invalid={hasErrors && !handleCcValidate(data)}
                                placeholder={getMessage('ccPlaceholder')}
                                size='small'
                                disabled={isDisabled}
                                onChange={handleChangeCc}
                                qa={qaCc}
                            />

                            <div>
                                <Label>{getMessage('replyToLabel')}</Label>

                                <Text font='label' fontSize='small' spacing='12 0 0 0'>
                                    {data.replyTo}
                                </Text>
                            </div>
                        </Grid>

                        <div>
                            <Label>{getMessage('attachmentsLabel')}</Label>

                            {labels?.attachmentsNote && (
                                <Box spacing='12 0'>
                                    <Alert startIcon={<AlertIcon />} size='small'>
                                        {labels.attachmentsNote}
                                    </Alert>
                                </Box>
                            )}

                            <Box spacing={labels?.attachmentsNote ? '0' : '4 0 0 0'}>
                                <AttachmentList
                                    attachments={attachments}
                                    newAttachments={attachmentsNew}
                                    attachmentKind={AttachmentKind.EmailToSupplierAttachment}
                                    isReadonly={isDisabled}
                                    size='xsmall'
                                    onRemoveAttachment={onAttachRemove}
                                    customHandleClickOnAttachment={handleClickOnAttachment}
                                />
                            </Box>
                        </div>
                    </Grid>

                    <Grid rowGap={24}>
                        <Grid rowGap={4}>
                            <Label required>{getMessage('subjectLabel')}</Label>

                            <RichEditor
                                dataQa={qaSubject}
                                value={subject || ''}
                                onChange={onChangeSubject}
                                invalid={hasErrors && !data.subject}
                                maxLength={constants.commonConstants.EMAIL_TO_SUPPLIER_SUBJECT_MAX_LENGTH}
                                placeholder={getMessage('subjectPlaceholder') ?? ''}
                                changeOnBlur
                                allowTextFormatting={false}
                                allowMention
                                customMentionConfig={customMentionConfig}
                                restrictNewLine
                                disabled={isDisabled}
                                minHeight={28}
                                size='small'
                            />
                        </Grid>

                        <Grid rowGap={4}>
                            <Label required>{getMessage('bodyLabel')}</Label>

                            <RichEditor
                                dataQa={qaBody}
                                value={body || ''}
                                onChange={onChangeBody}
                                invalid={hasErrors && !data.body}
                                maxLength={constants.commonConstants.EMAIL_TO_SUPPLIER_BODY_MAX_LENGTH}
                                placeholder={getMessage('bodyPlaceholder') ?? ''}
                                changeOnBlur
                                allowMention
                                customMentionConfig={customMentionConfig}
                                disabled={isDisabled}
                                size='small'
                            />
                        </Grid>
                    </Grid>
                </Grid>
            )}
        </Box>
    );
});

Form.displayName = 'Form';
